import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { Trash2 } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@atrvd/saas-ui'
import type { ManualBankFormValues } from './manualBankSchema'
import { MANUAL_BANK_COPY } from './manualBankCopy'
import { RequiredMark } from '@/components/ui/required-mark'
import type { FacilityOption } from './manualBanks.utils'

interface ManualAccountRowFieldsProps {
  formId: string
  index: number
  control: Control<ManualBankFormValues>
  register: UseFormRegister<ManualBankFormValues>
  errors: FieldErrors<ManualBankFormValues>
  typeOptions: ReadonlyArray<{ value: string; label: string }>
  facilityOptions: readonly FacilityOption[]
  canRemove: boolean
  onRemove: () => void
}

/**
 * One account-or-card row of the manual bank form: name, type, number,
 * facility and a remove button. Every row carries its own visible labels, as
 * in the prototype, so each control keeps an accessible name.
 */
export function ManualAccountRowFields({
  formId,
  index,
  control,
  register,
  errors,
  typeOptions,
  facilityOptions,
  canRemove,
  onRemove,
}: ManualAccountRowFieldsProps) {
  const rowErrors = errors.accounts?.[index]
  const id = (field: string) => `${formId}-${index}-${field}`
  const errorId = (field: string) => `${id(field)}-error`
  const hasErrors = !!(
    rowErrors?.name ||
    rowErrors?.account_type ||
    rowErrors?.account_number ||
    rowErrors?.facility_id
  )

  return (
    // Field errors are absolutely positioned so a long message never pushes
    // sibling controls out of line; the extra bottom padding, only while a
    // row has errors, keeps them inside the card.
    <div className={cn('rounded-xl border bg-slate-50 p-4', hasErrors && 'pb-8')}>
      <div className="grid grid-cols-[1fr_0.8fr_0.8fr_1fr_auto] items-end gap-3.5">
        <div className="space-y-1.5">
          <Label htmlFor={id('name')} className="text-xs font-semibold text-muted-foreground">
            {MANUAL_BANK_COPY.rowNameLabel}
            <RequiredMark />
          </Label>
          <div className="relative">
            <Input
              id={id('name')}
              placeholder={MANUAL_BANK_COPY.rowNamePlaceholder}
              aria-required="true"
              className="aria-[invalid=true]:border-destructive"
              aria-invalid={!!rowErrors?.name}
              aria-describedby={rowErrors?.name ? errorId('name') : undefined}
              {...register(`accounts.${index}.name`)}
            />
            {rowErrors?.name && (
              <p id={errorId('name')} className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                {rowErrors.name.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={id('type')} className="text-xs font-semibold text-muted-foreground">
            {MANUAL_BANK_COPY.typeLabel}
            <RequiredMark />
          </Label>
          <div className="relative">
            <Controller
              control={control}
              name={`accounts.${index}.account_type`}
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    // Lets RHF focus (and so scroll to) this select when it is the
                    // first invalid field on submit, as it does for inputs.
                    ref={field.ref}
                    id={id('type')}
                    className="aria-[invalid=true]:border-destructive"
                    aria-invalid={!!rowErrors?.account_type}
                    aria-describedby={rowErrors?.account_type ? errorId('type') : undefined}
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {rowErrors?.account_type && (
              <p id={errorId('type')} className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                {rowErrors.account_type.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={id('number')} className="text-xs font-semibold text-muted-foreground">
            {MANUAL_BANK_COPY.rowNumberLabel}
            <RequiredMark />
          </Label>
          <div className="relative">
            <Input
              id={id('number')}
              placeholder={MANUAL_BANK_COPY.numberPlaceholder}
              aria-required="true"
              className="aria-[invalid=true]:border-destructive"
              aria-invalid={!!rowErrors?.account_number}
              aria-describedby={rowErrors?.account_number ? errorId('number') : undefined}
              {...register(`accounts.${index}.account_number`)}
            />
            {rowErrors?.account_number && (
              <p id={errorId('number')} className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                {rowErrors.account_number.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={id('facility')} className="text-xs font-semibold text-muted-foreground">
            {MANUAL_BANK_COPY.facilityLabel}
            <RequiredMark />
          </Label>
          <div className="relative">
            <Controller
              control={control}
              name={`accounts.${index}.facility_id`}
              render={({ field }) => (
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger
                    // Lets RHF focus (and so scroll to) this select when it is the
                    // first invalid field on submit, as it does for inputs.
                    ref={field.ref}
                    id={id('facility')}
                    className="aria-[invalid=true]:border-destructive"
                    aria-invalid={!!rowErrors?.facility_id}
                    aria-describedby={rowErrors?.facility_id ? errorId('facility') : undefined}
                  >
                    <SelectValue placeholder={MANUAL_BANK_COPY.facilityPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {facilityOptions.map((facility) => (
                      <SelectItem key={facility.id} value={facility.id}>
                        {facility.name}
                        {facility.code && ` (${facility.code})`}
                        {facility.inactive && ' — inactive'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {rowErrors?.facility_id && (
              <p id={errorId('facility')} className="absolute left-0 top-full mt-0.5 text-xs text-destructive">
                {rowErrors.facility_id.message}
              </p>
            )}
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-10 w-10 border bg-background p-0 text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-destructive"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={`Remove account or card ${index + 1}`}
        >
          <Trash2 aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
